import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag, ShieldCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { rp } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { ConditionBadge } from "@/components/ConditionBadge";
import { toast } from "sonner";

export default function Checkout() {
  const { cart, removeFromCart, clearCart, brand } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [addr, setAddr] = useState({ recipient: user?.name || "", phone: user?.phone || "", address: "", city: "", postal_code: "" });
  const [method, setMethod] = useState("va");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });

  const codEligible = cart.length > 0 && cart.every((x) => x.cod_enabled);
  const isCod = method === "cod";

  const subtotal = cart.reduce((s, x) => s + x.price * (x.quantity || 1), 0);

  const placeOrder = async () => {
    if (!user) return navigate("/login", { state: { from: "/checkout" } });
    if (!addr.recipient || !addr.phone || !addr.address || !addr.city || !addr.postal_code)
      return toast.error("Lengkapi alamat pengiriman");
    setLoading(true);
    try {
      const items = cart.map((x) => ({ product_id: x.id, quantity: x.quantity || 1 }));
      const payment_mode = isCod ? "cod" : "prepaid";
      const { data } = await api.post("/orders", { items, shipping_address: addr, payment_mode });
      if (isCod) {
        clearCart();
        toast.success("Pesanan COD dibuat! Bayar langsung ke seller saat barang tiba.");
        navigate("/pesanan");
        return;
      }
      let tarapayOrderId = null;
      for (const o of data.orders) {
        const { data: pay } = await api.post(`/orders/${o.id}/pay`, { method });
        if (pay.provider === "tarapay" && !tarapayOrderId) tarapayOrderId = o.id;
      }
      clearCart();
      if (tarapayOrderId) {
        navigate(`/bayar/${tarapayOrderId}`);
      } else {
        toast.success("Pembayaran berhasil (simulasi). Pesanan dibuat!");
        navigate("/pesanan");
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  if (cart.length === 0) return (
    <Layout><div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <ShoppingBag className="w-14 h-14 text-rl-muted mx-auto" />
      <h1 className="mt-4 font-serif text-2xl font-semibold">Keranjang kosong</h1>
      <button onClick={() => navigate("/belanja")} className="mt-6 rounded-full bg-rl-primary text-white font-bold px-6 py-3">Mulai Belanja</button>
    </div></Layout>
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <h1 className="font-serif text-3xl font-semibold text-rl-ink">Checkout</h1>
        <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="space-y-6">
            {/* Items */}
            <div className="space-y-3">
              {cart.map((x) => (
                <div key={x.id} data-testid={`cart-item-${x.id}`} className="flex gap-4 rounded-xl border border-rl-border bg-white p-3">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-rl-bg2 shrink-0">{x.photo && <img src={x.photo} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-rl-ink text-sm">{x.title}</div>
                    <div className="mt-1"><ConditionBadge condition={x.condition} /></div>
                    <div className="text-xs text-rl-muted mt-1">{x.store}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-rl-ink">{rp(x.price)}</div>
                    <button data-testid={`cart-remove-${x.id}`} onClick={() => removeFromCart(x.id)} className="mt-2 text-rl-muted hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            {/* Address */}
            <div className="rounded-2xl border border-rl-border bg-white p-5">
              <h3 className="font-bold text-rl-ink mb-4">Alamat Pengiriman</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="addr-recipient" value={addr.recipient} onChange={set("recipient")} placeholder="Nama penerima" className="rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
                <input data-testid="addr-phone" value={addr.phone} onChange={set("phone")} placeholder="No. HP" className="rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
                <input data-testid="addr-address" value={addr.address} onChange={set("address")} placeholder="Alamat lengkap" className="sm:col-span-2 rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
                <input data-testid="addr-city" value={addr.city} onChange={set("city")} placeholder="Kota" className="rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
                <input data-testid="addr-postal" value={addr.postal_code} onChange={set("postal_code")} placeholder="Kode pos" className="rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
              </div>
            </div>
            {/* Payment method */}
            <div className="rounded-2xl border border-rl-border bg-white p-5">
              <h3 className="font-bold text-rl-ink mb-1">Metode Pembayaran</h3>
              <p className="text-xs text-rl-muted mb-4">{isCod
                ? <>COD — bayar tunai/QRIS langsung ke seller saat barang diterima. RELOVA menengahi bila ada sengketa.</>
                : <>Diproses via partner pembayaran {brand?.brand_name}. Dana ditahan (escrow) & dirilis ke seller setelah Anda konfirmasi terima, atau otomatis 3 hari.</>}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {/* {[["va", "Virtual Account"], ["ewallet", "E-Wallet"], ["qris", "QRIS"]].map(([k, label]) => (
                  <button key={k} data-testid={`pay-method-${k}`} onClick={() => setMethod(k)}
                    className={`rounded-xl border p-3 text-sm font-semibold transition-colors ${method === k ? "border-rl-primary bg-rl-primary/5 text-rl-primary" : "border-rl-border text-rl-ink2"}`}>{label}</button>
                ))} */}
                {[["qris", "QRIS"]].map(([k, label]) => (
                  <button key={k} data-testid={`pay-method-${k}`} onClick={() => setMethod(k)}
                    className={`rounded-xl border p-3 text-sm font-semibold transition-colors ${method === k ? "border-rl-primary bg-rl-primary/5 text-rl-primary" : "border-rl-border text-rl-ink2"}`}>{label}</button>
                ))}
                {codEligible && (
                  <button data-testid="pay-method-cod" onClick={() => setMethod("cod")}
                    className={`rounded-xl border p-3 text-sm font-semibold transition-colors ${isCod ? "border-rl-primary bg-rl-primary/5 text-rl-primary" : "border-rl-border text-rl-ink2"}`}>COD</button>
                )}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:sticky lg:top-20 h-fit rounded-2xl border border-rl-border bg-white p-5">
            <h3 className="font-bold text-rl-ink">Ringkasan</h3>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-rl-muted">Subtotal ({cart.length} barang)</span><span className="font-semibold">{rp(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-rl-muted">Ongkir</span><span className="font-semibold text-rl-secondary">{'Gratis Pengiriman'}</span></div>
              <div className="border-t border-rl-border pt-3 flex justify-between text-base"><span className="font-bold">Total</span><span className="font-bold text-rl-ink">{rp(subtotal)}</span></div>
            </div>
            <button data-testid="place-order" onClick={placeOrder} disabled={loading}
              className="mt-5 w-full rounded-full bg-rl-primary hover:bg-rl-primaryHover text-white font-bold py-3.5 transition-colors disabled:opacity-50">
              {loading ? "Memproses..." : isCod ? "Buat Pesanan COD" : "Bayar Sekarang"}</button>
            <p className="mt-3 flex items-center gap-1.5 text-xs text-rl-muted"><ShieldCheck className="w-4 h-4 text-rl-secondary" />Transaksi aman & dilindungi.</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
